import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { CityProvider } from './context/CityContext';
import { LanguageProvider } from './context/LanguageContext';
import { WishlistProvider } from './context/WishlistContext';
import { CouponProvider } from './context/CouponContext';
import { CategoryProvider } from './context/CategoryContext';
import { ContentProvider } from './context/ContentContext';
import { SearchProvider } from './context/SearchContext';
import { InventoryProvider } from './context/InventoryContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import { OAuthProvider } from './context/OAuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { EmailVerificationProvider } from './context/EmailVerificationContext';
import Banner from './components/Banner';
import Navigation from './components/Navigation';
import BackButton from './components/BackButton';
import Footer from './components/Footer';
import AdminProtectedRoute from './admin/components/AdminProtectedRoute';
import AdminLayout from './admin/components/AdminLayout';
import CookieConsentBanner from './components/CookieConsentBanner';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import WhatsAppWidget from './components/WhatsAppWidget';
import LoadingSpinner from './components/LoadingSpinner';
import AccessibilityHelper from './components/AccessibilityHelper';

// Lazy load pages for better performance
const Home = React.lazy(() => import('./pages/Home'));
const Products = React.lazy(() => import('./pages/Products'));
const Campaigns = React.lazy(() => import('./pages/Campaigns'));
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Login = React.lazy(() => import('./pages/Login'));
const OAuthCallback = React.lazy(() => import('./pages/OAuthCallback'));
const Profile = React.lazy(() => import('./pages/Profile'));
const DogalMalzemeler = React.lazy(() => import('./pages/DogalMalzemeler'));
const UstaEller = React.lazy(() => import('./pages/UstaEller'));
const HizliTeslimat = React.lazy(() => import('./pages/HizliTeslimat'));
const Cart = React.lazy(() => import('./pages/Cart'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const OrderSuccess = React.lazy(() => import('./pages/OrderSuccess'));
const Register = React.lazy(() => import('./pages/Register'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const AdminLogin = React.lazy(() => import('./admin/pages/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./admin/pages/AdminDashboard'));
const AdminProducts = React.lazy(() => import('./admin/pages/AdminProducts'));
const AdminCategories = React.lazy(() => import('./admin/pages/AdminCategories'));
const AdminContent = React.lazy(() => import('./admin/pages/AdminContent'));
const AdminPageContent = React.lazy(() => import('./admin/pages/AdminPageContent'));
const AdminOrders = React.lazy(() => import('./admin/pages/AdminOrders'));
const AdminProductForm = React.lazy(() => import('./admin/pages/AdminProductForm'));
const AdminCustomers = React.lazy(() => import('./admin/pages/AdminCustomers'));
const AdminCustomerDetail = React.lazy(() => import('./admin/pages/AdminCustomerDetail'));
const AdminCoupons = React.lazy(() => import('./admin/pages/AdminCoupons'));
const AdminBanners = React.lazy(() => import('./admin/pages/AdminBanners'));
const AdminFlipbook = React.lazy(() => import('./admin/pages/AdminFlipbook'));
const AdminDealCampaigns = React.lazy(() => import('./admin/pages/AdminDealCampaigns'));
const AdminCollections = React.lazy(() => import('./admin/pages/AdminCollections'));
const AdminCargoSettings = React.lazy(() => import('./admin/pages/AdminCargoSettings'));
const AdminDeliverySchedules = React.lazy(() => import('./admin/pages/AdminDeliverySchedules'));
const AdminSettings = React.lazy(() => import('./admin/pages/AdminSettings'));
const AdminReviews = React.lazy(() => import('./admin/pages/AdminReviews'));
const AdminReports = React.lazy(() => import('./admin/pages/AdminReports'));
const AdminPaymentSettings = React.lazy(() => import('./admin/pages/AdminPaymentSettings'));
const AdminReturns = React.lazy(() => import('./admin/pages/AdminReturns'));
const AdminAuditLogs = React.lazy(() => import('./admin/pages/AdminAuditLogs'));
const AdminEmailTemplates = React.lazy(() => import('./admin/pages/AdminEmailTemplates'));
const AdminUsers = React.lazy(() => import('./admin/pages/AdminUsers'));
const OrderTracking = React.lazy(() => import('./pages/OrderTracking'));
const Wishlist = React.lazy(() => import('./pages/Wishlist'));
const Support = React.lazy(() => import('./pages/Support'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = React.lazy(() => import('./pages/TermsOfUse'));
const ReturnPolicy = React.lazy(() => import('./pages/ReturnPolicy'));
const DistanceSalesAgreement = React.lazy(() => import('./pages/DistanceSalesAgreement'));
const WithdrawalForm = React.lazy(() => import('./pages/WithdrawalForm'));
const ReturnRequest = React.lazy(() => import('./pages/ReturnRequest'));
const CookiePolicy = React.lazy(() => import('./pages/CookiePolicy'));
const EmailVerification = React.lazy(() => import('./pages/EmailVerification'));
const ResendVerification = React.lazy(() => import('./pages/ResendVerification'));
const PaymentReturn = React.lazy(() => import('./pages/PaymentReturn'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));

const PublicBanner = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return <Banner />;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CityProvider>
          <OAuthProvider>
            <NotificationProvider>
            <CookieConsentProvider>
              <EmailVerificationProvider>
                <CartProvider>
                <WishlistProvider>
                  <CouponProvider>
                    <CategoryProvider>
                    <ContentProvider>
                      <SearchProvider>
                        <InventoryProvider>
                          <AnalyticsProvider>
                            <Router>
                            <AccessibilityHelper />
                            <div className="min-h-screen bg-gray-50 dark:bg-stone-950 transition-colors duration-300">
                            <PublicBanner />
                            <Routes>
                              <Route path="/admin/*" element={
                                <div>
                                  <Routes>
                                    <Route path="/login" element={<AdminLogin />} />
                                    <Route 
                                      path="/dashboard" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminDashboard />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route 
                                      path="/products" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminProducts />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route 
                                      path="/orders" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminOrders />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route
                                      path="/returns"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminReturns />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/audit-logs"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminAuditLogs />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/email-templates"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminEmailTemplates />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/products/new"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminProductForm />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route 
                                      path="/products/edit/:id" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminProductForm />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route 
                                      path="/categories" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminCategories />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    {/* <Route
                                      path="/content"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminContent />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    /> */}
                                    <Route
                                      path="/page-content"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminPageContent />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/customers"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminCustomers />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route 
                                      path="/customers/:id" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminCustomerDetail />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route 
                                      path="/coupons" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminCoupons />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route
                                      path="/banners"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminBanners />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/flipbook"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminFlipbook />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/deal-campaigns"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminDealCampaigns />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/collections"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminCollections />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/cargo-settings"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminCargoSettings />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route 
                                      path="/delivery-schedules" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminDeliverySchedules />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route 
                                      path="/settings" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminSettings />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route 
                                      path="/reviews" 
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminReviews />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      } 
                                    />
                                    <Route
                                      path="/reports"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminReports />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/payment-settings"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminPaymentSettings />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/admins"
                                      element={
                                        <AdminProtectedRoute>
                                          <AdminLayout>
                                            <AdminUsers />
                                          </AdminLayout>
                                        </AdminProtectedRoute>
                                      }
                                    />
                                  </Routes>
                                </div>
                              } />
                              <Route path="/*" element={
                                <div>
                                  <Navigation />
                                  <BackButton />
                                  <Suspense fallback={
                                    <div className="min-h-screen flex items-center justify-center">
                                      <LoadingSpinner size="lg" text="Yükleniyor... / Loading..." />
                                    </div>
                                  }>
                                    <Routes>
                                      <Route path="/" element={<Home />} />
                                      <Route path="/products" element={<Products />} />
                                      <Route path="/products/:category" element={<Products />} />
                                      <Route path="/products/:category/:subcategory" element={<Products />} />
                                      <Route path="/campaigns" element={<Campaigns />} />
                                      <Route path="/product/:param/:extra?" element={<ProductDetail />} />
                                      <Route path="/about" element={<About />} />
                                      <Route path="/contact" element={<Contact />} />
                                      <Route path="/login" element={<Login />} />
                                      <Route path="/oauth2/callback" element={<OAuthCallback />} />
                                      <Route path="/register" element={<Register />} />
                                      <Route path="/profile" element={<Profile />} />
                                      <Route path="/dogal-malzemeler" element={<DogalMalzemeler />} />
                                      <Route path="/usta-eller" element={<UstaEller />} />
                                      <Route path="/hizli-teslimat" element={<HizliTeslimat />} />
                                      <Route path="/cart" element={<Cart />} />
                                      <Route path="/wishlist" element={<Wishlist />} />
                                      <Route path="/support" element={<Support />} />
                                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                      <Route path="/privacy" element={<PrivacyPolicy />} />
                                      <Route path="/terms-of-use" element={<TermsOfUse />} />
                                      <Route path="/terms" element={<TermsOfUse />} />
                                      <Route path="/return-policy" element={<ReturnPolicy />} />
                                      <Route path="/return" element={<ReturnPolicy />} />
                                      <Route path="/refund" element={<ReturnPolicy />} />
                                      <Route path="/return-request" element={<ReturnRequest />} />
                                      <Route path="/cookie-policy" element={<CookiePolicy />} />
                                      <Route path="/cookies" element={<CookiePolicy />} />
                                      <Route path="/distance-sales-agreement" element={<DistanceSalesAgreement />} />
                                      <Route path="/mesafeli-satis-sozlesmesi" element={<DistanceSalesAgreement />} />
                                      <Route path="/withdrawal-form" element={<WithdrawalForm />} />
                                      <Route path="/herroepingsformulier" element={<WithdrawalForm />} />
                                      <Route path="/formulaire-de-retractation" element={<WithdrawalForm />} />
                                      <Route path="/verify-email/:token" element={<EmailVerification />} />
                                      <Route path="/resend-verification" element={<ResendVerification />} />
                                      <Route path="/checkout" element={<Checkout />} />
                                      <Route path="/order-success" element={<OrderSuccess />} />
                                      <Route path="/order-tracking" element={<OrderTracking />} />
                                      <Route path="/order-tracking/:orderId" element={<OrderTracking />} />
                                      <Route path="/payment/return" element={<PaymentReturn />} />
                                      <Route path="/forgot-password" element={<ForgotPassword />} />
                                      <Route path="/reset-password" element={<ResetPassword />} />
                                    </Routes>
                                    </Suspense>
                                  <Footer />
                                  <CookieConsentBanner />
                                  <OfflineBanner />
                                  <WhatsAppWidget />
                                </div>
                              } />
                            </Routes>
                          </div>
                        </Router>
                            </AnalyticsProvider>
                          </InventoryProvider>
                        </SearchProvider>
                    </ContentProvider>
                    </CategoryProvider>
                    </CouponProvider>
                  </WishlistProvider>
                </CartProvider>
              </EmailVerificationProvider>
              </CookieConsentProvider>
            </NotificationProvider>
          </OAuthProvider>
          </CityProvider>
        </AuthProvider>
      </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
